import { ROLES } from '../../../../../constants/constants';

const ADMIN_ROLES = new Set([
  ROLES.Admin,
  ROLES.MarketAdmin,
  ROLES.CountryAdmin,
  ROLES.SuperAdmin,
]);

const COUNTRY_LANGUAGE_OPTIONS = {
  EN: ['en'],
  AT: ['de-AT'],
  BE: ['fr-BE', 'nl-BE'],
  CH: ['de-CH', 'fr-CH', 'it-CH'],
  CZ: ['cs'],
  DE: ['de-DE'],
  ES: ['es'],
  FR: ['fr-FR'],
  IE: ['en-IE'],
  IT: ['it-IT'],
  LUX: ['fr-LUX'],
  NL: ['nl-NL'],
  NO: ['no'],
  PL: ['pl'],
  PT: ['pt'],
  RO: ['ro'],
  SE: ['se'],
  SK: ['sk'],
  TR: ['tr'],
};

export const MASTER_COUNTRY = 'MASTER';

const COUNTRY_NAMES = {
  EN: 'England',
  AT: 'Austria',
  BE: 'Belgium',
  CH: 'Switzerland',
  CZ: 'Czech Republic',
  DE: 'Germany',
  ES: 'Spain',
  FR: 'France',
  IE: 'Ireland',
  IT: 'Italy',
  LUX: 'Luxembourg',
  NL: 'Netherlands',
  NO: 'Norway',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SE: 'Sweden',
  SK: 'Slovakia',
  TR: 'Turkey',
};

const DEFAULT_COUNTRY_OPTIONS = Object.values(COUNTRY_NAMES);

const normalizeString = (value) => String(value || '').trim();

const normalizeCode = (value) => normalizeString(value).toLowerCase();

const normalizeCountryName = (value) => {
  const rawCountry = normalizeString(value);
  if (!rawCountry) {
    return '';
  }

  const countryCode = rawCountry.toUpperCase();
  if (COUNTRY_NAMES[countryCode]) {
    return COUNTRY_NAMES[countryCode];
  }

  return (
    Object.values(COUNTRY_NAMES).find(
      (countryName) => countryName.toLowerCase() === rawCountry.toLowerCase()
    ) || rawCountry
  );
};

export const getCountryCode = (value) => {
  const countryName = normalizeCountryName(value);
  return (
    Object.entries(COUNTRY_NAMES).find(
      ([, name]) => name.toLowerCase() === countryName.toLowerCase()
    )?.[0] || ''
  );
};

const normalizeLanguageCode = (value) => {
  const normalized = normalizeCode(value);
  const knownCode = Object.values(COUNTRY_LANGUAGE_OPTIONS)
    .flat()
    .find((code) => code.toLowerCase() === normalized);

  return knownCode || normalizeString(value);
};

const toArray = (value) => {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

const extractFileName = (pathValue = '') => {
  const raw = normalizeString(pathValue);
  if (!raw) {
    return '';
  }

  const pathChunks = raw.split('/');
  const lastChunk = pathChunks[pathChunks.length - 1] || raw;
  return lastChunk.split('\\').pop() || lastChunk;
};

const getDisplayStatus = (statusValue) => {
  const status = normalizeCode(statusValue);

  if (status === 'pro') {
    return 'pro';
  }

  if (status === 'pre') {
    return 'pre';
  }

  return 'pending';
};

const normalizeBooleanFlag = (value) =>
  value === true || value === 1 || value === '1';

const normalizeFileId = (value) => {
  if (typeof value === 'number') {
    return value;
  }

  const normalizedId = normalizeString(value);
  if (!/^\d+$/.test(normalizedId)) {
    return value;
  }

  const numericId = Number(normalizedId);
  return Number.isSafeInteger(numericId) && numericId > 0 ? numericId : value;
};

export const mapTranslationFile = (rawFile = {}) => {
  const fileName =
    normalizeString(rawFile.filename) ||
    extractFileName(rawFile.excel_path) ||
    extractFileName(rawFile.file_path);

  return {
    id: normalizeFileId(rawFile.id),
    fileName,
    country: normalizeString(rawFile.country),
    language: normalizeString(rawFile.language),
    type: normalizeString(rawFile.type),
    uploadedAt: rawFile.uploaded_at || null,
    hasPendingKeys: normalizeBooleanFlag(rawFile.has_pending_keys),
    needsReview: normalizeBooleanFlag(rawFile.needs_review),
    status: getDisplayStatus(rawFile.status),
    publishedPreAt: rawFile.published_pre_at || null,
    publishedProAt: rawFile.published_pro_at || null,
  };
};

export const shouldShowTranslationUpdate = (file, role) => {
  if (role === ROLES.Translator) {
    return file.hasPendingKeys;
  }

  if (role === ROLES.Reviewer) {
    return file.needsReview;
  }

  return ADMIN_ROLES.has(role) && (file.hasPendingKeys || file.needsReview);
};

const buildUniqueSorted = (values = []) =>
  [...new Set(values.map(normalizeString).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );

const getUserLanguageAssignments = (loggedUser = {}) => {
  const assignments = [...toArray(loggedUser.languages)];

  if (loggedUser.area_code && loggedUser.preferred_language) {
    assignments.push({
      country: loggedUser.area_code,
      language: loggedUser.preferred_language,
    });
  }

  return assignments;
};

export const getCountryOptions = (loggedUser = {}, files = [], role = '') => {
  const fromUser = buildUniqueSorted(
    getUserLanguageAssignments(loggedUser).map((item) => item?.country)
  );

  if (role === 'translator' || role === 'reviewer') {
    return fromUser;
  }

  if (role === 'admin' || role === 'marketAdmin' || role === 'countryAdmin') {
    return buildUniqueSorted(loggedUser.managed_area_codes);
  }

  if (fromUser.length) {
    return fromUser;
  }

  const fromFiles = buildUniqueSorted(
    files.map((item) => normalizeCountryName(item.country))
  );
  if (fromFiles.length) {
    return fromFiles;
  }

  return DEFAULT_COUNTRY_OPTIONS;
};

export const getLanguageOptions = (
  loggedUser = {},
  files = [],
  country = '',
  role = '',
  type = ''
) => {
  if (role === 'admin' && country === MASTER_COUNTRY) {
    return ['en'];
  }

  const normalizedCountry = normalizeCode(normalizeCountryName(country));

  const fromUser = buildUniqueSorted(
    getUserLanguageAssignments(loggedUser)
      .filter(
        (item) =>
          normalizeCode(normalizeCountryName(item?.country)) === normalizedCountry
      )
      .map((item) => normalizeLanguageCode(item?.language))
  );

  if (role === 'translator' || role === 'reviewer') {
    return fromUser;
  }

  if (role === 'admin' || role === 'marketAdmin' || role === 'countryAdmin') {
    const countryLanguages = COUNTRY_LANGUAGE_OPTIONS[getCountryCode(country)] || [];
    return countryLanguages;
  }

  if (fromUser.length) {
    return fromUser;
  }

  const fromFiles = buildUniqueSorted(
    files
      .filter(
        (item) =>
          normalizeCode(normalizeCountryName(item.country)) === normalizedCountry
      )
      .map((item) => normalizeLanguageCode(item.language))
  );

  return fromFiles;
};

export { extractFileName };
